import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sendToTerminal } from "../../../../../lib/terminalLogger";
import { LogCategory } from "../../../../../config/logging";
import { calculateQuotation } from "../../services/quotationService";
import type { OrderObject, ProductRule, SavingRule } from "../../types";
import type { Quality } from "../QualitySelectModal";
import type { Cliente } from "../../../../../types/gerArquivos";
import type { NotifyFn } from "./QuotationSummary.types";
import {
	calculateQuotationMetrics,
	formatMeasurementInput,
	getQuotationResultTotals,
	normalizeEditedMeasurement,
} from "./QuotationSummary.utils";

interface UseQuotationCalculationStateParams {
	objects: OrderObject[];
	selectedRule: ProductRule;
	selectedQuality: Quality | null;
	savingRule: SavingRule | null;
	selectedClient: Cliente | null;
	manualClientName: string;
	notify: NotifyFn;
}

export function useQuotationCalculationState({
	objects,
	selectedRule,
	selectedQuality,
	savingRule,
	selectedClient,
	manualClientName,
	notify,
}: UseQuotationCalculationStateParams) {
	const isCalculatingRef = useRef(false);
	const calculationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isMountedRef = useRef(true);

	const [isCalculating, setIsCalculating] = useState(false);
	const [quotationResult, setQuotationResult] = useState<any>(null);
	const [error, setError] = useState<string | null>(null);
	const [isEditingMetragem, setIsEditingMetragem] = useState(false);
	const [editedMetragem, setEditedMetragem] = useState<number | null>(null);
	const [editedMetragemString, setEditedMetragemString] = useState("");
	const [confirmedMetragem, setConfirmedMetragem] = useState<number | null>(null);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			if (calculationTimeoutRef.current) {
				clearTimeout(calculationTimeoutRef.current);
			}
		};
	}, []);

	const { totalMetragem, totalUnidades, isMetros } = useMemo(
		() => calculateQuotationMetrics(objects, savingRule),
		[objects, savingRule],
	);

	const minMetragem = useMemo(
		() => (isMetros ? totalMetragem : totalUnidades),
		[isMetros, totalMetragem, totalUnidades],
	);

	const finalMetragem = useMemo(() => confirmedMetragem, [confirmedMetragem]);

	useEffect(() => {
		if (!selectedRule || !savingRule) {
			setQuotationResult(null);
			return;
		}

		if (isCalculatingRef.current || !isMountedRef.current) {
			return;
		}

		const shouldCalculate = !selectedClient || (selectedClient && selectedClient.id);
		if (!shouldCalculate) return;

		if (calculationTimeoutRef.current) {
			clearTimeout(calculationTimeoutRef.current);
		}

		calculationTimeoutRef.current = setTimeout(() => {
			if (!isMountedRef.current || isCalculatingRef.current || !selectedRule || !savingRule) {
				return;
			}

			const calculateAll = async () => {
				if (!isMountedRef.current || isCalculatingRef.current || !selectedRule || !savingRule) {
					return;
				}

				isCalculatingRef.current = true;
				setIsCalculating(true);
				setError(null);

				try {
					const proId = selectedRule.produto?.id ?? 0;
					const categoriaId = selectedRule.produto?.categoria_item ?? 0;
					const qualidadeId = selectedQuality?.id ?? 0;

					if (!proId || !categoriaId || !qualidadeId) {
						throw new Error("Dados incompletos para calculo");
					}

					const metragem =
						confirmedMetragem !== null
							? confirmedMetragem
							: isMetros
								? totalMetragem
								: totalUnidades;

					if (!metragem || metragem <= 0) {
						throw new Error("Metragem invalida. Verifique os objetos selecionados.");
					}

					const result = await calculateQuotation({
						cli_id: selectedClient ? Number(selectedClient.id) : null,
						pro_id: proId,
						qualidade_id: qualidadeId,
						categoria_id: categoriaId,
						metragem,
						produtoUnd: savingRule.umd_id,
						contexto: "default",
					});

					if (!result || result.valor_total <= 0 || result.preco_unitario <= 0) {
						throw new Error("Resultado do calculo invalido. Verifique os dados do produto.");
					}

					if (!isMountedRef.current) return;

					setQuotationResult(result);
					setError(null);

					sendToTerminal(
						"[QUOTATION] Orcamento calculado com sucesso",
						{
							fonte: result.fonte,
							valorTotal: result.valor_total,
							precoUnitario: result.preco_unitario,
							metragem,
							clienteId: selectedClient?.id || null,
							clienteNome: selectedClient?.nome || manualClientName || "Nao informado",
						},
						LogCategory.BUSINESS,
					);
				} catch (err) {
					if (!isMountedRef.current) return;

					const errorMessage = err instanceof Error ? err.message : String(err);
					notify({
						type: "error",
						title: "Erro ao Calcular Orcamento",
						message: errorMessage,
						sticky: false,
						duration: 10000,
					});

					setError(errorMessage);
					setQuotationResult(null);
					sendToTerminal(
						"[QUOTATION] Erro ao calcular orcamento",
						{
							error: errorMessage,
							proId: selectedRule.produto?.id,
							categoriaId: selectedRule.produto?.categoria_item ?? 0,
							qualidadeId: selectedQuality?.id,
							metragem:
								confirmedMetragem !== null
									? confirmedMetragem
									: isMetros
										? totalMetragem
										: totalUnidades,
							clienteId: selectedClient?.id || null,
						},
						LogCategory.ERROR,
					);
				} finally {
					if (isMountedRef.current) {
						isCalculatingRef.current = false;
						setIsCalculating(false);
					}
				}
			};

			void calculateAll();
		}, 300);

		return () => {
			if (calculationTimeoutRef.current) {
				clearTimeout(calculationTimeoutRef.current);
			}
		};
	}, [
		confirmedMetragem,
		isMetros,
		manualClientName,
		notify,
		savingRule,
		selectedClient,
		selectedQuality,
		selectedRule,
		totalMetragem,
		totalUnidades,
	]);

	const { precoFinal, valorTotalFinal, descontoAplicado, fonteCalculo } =
		useMemo(() => getQuotationResultTotals(quotationResult), [quotationResult]);

	const handleEditedMetragemChange = useCallback((value: string) => {
		const normalized = normalizeEditedMeasurement(value);
		setEditedMetragemString(normalized.displayValue);
	}, []);

	const handleEditedMetragemBlur = useCallback(() => {
		const normalizedValue = editedMetragemString.replace(",", ".");
		const numValue = Number.parseFloat(normalizedValue);

		if (Number.isNaN(numValue) || numValue < minMetragem) {
			setEditedMetragemString(formatMeasurementInput(minMetragem, isMetros));
			setEditedMetragem(minMetragem);
			return;
		}

		setEditedMetragem(numValue);
		setEditedMetragemString(formatMeasurementInput(numValue, isMetros));
	}, [editedMetragemString, isMetros, minMetragem]);

	const handleToggleEditingMetragem = useCallback(
		(enabled: boolean) => {
			setIsEditingMetragem(enabled);

			if (!enabled) {
				setEditedMetragem(null);
				setEditedMetragemString("");
				if (
					confirmedMetragem !== null &&
					confirmedMetragem === (isMetros ? totalMetragem : totalUnidades)
				) {
					setConfirmedMetragem(null);
				}
				return;
			}

			const initialValue =
				confirmedMetragem !== null
					? confirmedMetragem
					: isMetros
						? totalMetragem
						: totalUnidades;

			setEditedMetragem(initialValue);
			setEditedMetragemString(formatMeasurementInput(initialValue, isMetros));
		},
		[confirmedMetragem, isMetros, totalMetragem, totalUnidades],
	);

	const handleConfirmEditedMetragem = useCallback(() => {
		const normalizedValue = editedMetragemString.replace(",", ".");
		const numValue = Number.parseFloat(normalizedValue);
		const valueToConfirm =
			!Number.isNaN(numValue) && numValue >= minMetragem ? numValue : minMetragem;

		setConfirmedMetragem(valueToConfirm);
		setEditedMetragem(valueToConfirm);
		setEditedMetragemString(formatMeasurementInput(valueToConfirm, isMetros));

		sendToTerminal(
			"[QUOTATION] Metragem editada e confirmada",
			{
				metragemAnterior:
					confirmedMetragem !== null
						? confirmedMetragem
						: isMetros
							? totalMetragem
							: totalUnidades,
				metragemNova: valueToConfirm,
				metragemMinima: minMetragem,
				isMetros,
			},
			LogCategory.BUSINESS,
		);
	}, [
		confirmedMetragem,
		editedMetragemString,
		isMetros,
		minMetragem,
		totalMetragem,
		totalUnidades,
	]);

	return {
		isCalculating,
		quotationResult,
		error,
		totalMetragem,
		totalUnidades,
		isMetros,
		minMetragem,
		finalMetragem,
		precoFinal,
		valorTotalFinal,
		descontoAplicado,
		fonteCalculo,
		isEditingMetragem,
		editedMetragem,
		editedMetragemString,
		confirmedMetragem,
		handleEditedMetragemChange,
		handleEditedMetragemBlur,
		handleToggleEditingMetragem,
		handleConfirmEditedMetragem,
	};
}
